import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  ChevronRight, Check, X, RefreshCw, Unplug, Plug,
  ExternalLink, AlertTriangle,
} from "lucide-react";

// ─── ATS metadata ─────────────────────────────────────────────────────────────

const ATS_META = {
  greenhouse: {
    name: "Greenhouse",
    color: "#24A47F",
    logo: (
      <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8">
        <rect width="40" height="40" rx="6" fill="#24A47F" />
        <path d="M20 8C13.37 8 8 13.37 8 20s5.37 12 12 12 12-5.37 12-12S26.63 8 20 8zm0 21c-4.97 0-9-4.03-9-9s4.03-9 9-9 9 4.03 9 9-4.03 9-9 9z" fill="white" />
        <circle cx="20" cy="20" r="4" fill="white" />
      </svg>
    ),
    apiKeyLabel: "Harvest API Key",
    apiKeyHelp: "Greenhouse → Settings → Dev Center → API Credential Management → Harvest API Key",
    apiKeyLink: "https://app.greenhouse.io/configure/dev_center/credentials",
    docsUrl: "https://developers.greenhouse.io/harvest.html",
  },
  lever: {
    name: "Lever",
    color: "#5C4BE1",
    logo: (
      <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8">
        <rect width="40" height="40" rx="6" fill="#5C4BE1" />
        <text x="8" y="28" fontSize="22" fontWeight="bold" fill="white" fontFamily="sans-serif">L</text>
      </svg>
    ),
    apiKeyLabel: "API Key",
    apiKeyHelp: "Lever → Settings → Integrations & API → API Credentials",
    apiKeyLink: "https://hire.lever.co/settings/integrations",
    docsUrl: "https://hire.lever.co/developer/documentation",
  },
};

const COMING_SOON = [
  { name: "Workday", note: "No public API — use URL import on the New Role page." },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelTime(isoStr) {
  if (!isoStr) return null;
  const diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ConnectedActions({ name, conn, onSync, onDisconnect }) {
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try { await onSync(); } finally { setSyncing(false); }
  };
  const handleDisconnect = async () => {
    setDisconnecting(true);
    try { await onDisconnect(); setConfirmDisconnect(false); } finally { setDisconnecting(false); }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border p-3">
          <p className="font-mono text-[10px] text-muted-foreground tracking-[0.08em] uppercase mb-1">Last Synced</p>
          <p className="text-sm font-medium">{conn.last_synced_at ? formatRelTime(conn.last_synced_at) : "Never"}</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="font-mono text-[10px] text-muted-foreground tracking-[0.08em] uppercase mb-1">Roles Imported</p>
          <p className="text-sm font-medium">{conn.last_sync_imported ?? 0} last sync</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-all disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync Now"}
        </button>
        {!confirmDisconnect ? (
          <button onClick={() => setConfirmDisconnect(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-mono border border-[#E11D48]/30 text-[#E11D48] hover:bg-[#E11D48]/5 transition-all">
            <Unplug className="w-3.5 h-3.5" /> Disconnect
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono">Disconnect {name}?</span>
            <button onClick={handleDisconnect} disabled={disconnecting}
              className="px-2.5 py-1.5 rounded-lg text-xs font-mono bg-[#E11D48] text-white hover:bg-[#BE123C] transition-colors disabled:opacity-50">
              {disconnecting ? "…" : "Yes, disconnect"}
            </button>
            <button onClick={() => setConfirmDisconnect(false)}
              className="px-2.5 py-1.5 text-xs font-mono rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground font-mono">
        Sync imports open roles — existing roles are never overwritten.
      </p>
    </div>
  );
}

// ─── Greenhouse / Lever card (API key only) ───────────────────────────────────

function ATSCard({ atsType, conn, onConnect, onDisconnect, onSync }) {
  const meta = ATS_META[atsType];
  const connected = !!conn;
  const hasError = conn?.status === "error";
  const [apiKey, setApiKey] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!apiKey.trim()) { toast.error("API key is required"); return; }
    setConnecting(true);
    try {
      await onConnect(atsType, { api_key: apiKey.trim() });
      setApiKey(""); setShowForm(false);
    } finally { setConnecting(false); }
  };

  return (
    <div className={`border bg-white ${hasError ? "border-[#E11D48]/40" : "border-border"}`}>
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-center gap-3">
          {meta.logo}
          <div>
            <p className="font-semibold text-sm">{meta.name}</p>
            {connected && !hasError && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#10B981] tracking-[0.08em]">
                <Check className="w-3 h-3" /> Connected
              </span>
            )}
            {hasError && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#E11D48] tracking-[0.08em]">
                <AlertTriangle className="w-3 h-3" /> Connection error
              </span>
            )}
            {!connected && (
              <span className="font-mono text-[10px] text-muted-foreground tracking-[0.08em]">Not connected</span>
            )}
          </div>
        </div>
        <a href={meta.docsUrl} target="_blank" rel="noopener noreferrer"
          className="font-mono text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
          API docs <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {connected ? (
        <ConnectedActions
          name={meta.name} conn={conn}
          onSync={() => onSync(atsType)}
          onDisconnect={() => onDisconnect(atsType)}
        />
      ) : (
        <div className="p-5 space-y-4">
          {!showForm ? (
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-all">
              <Plug className="w-3.5 h-3.5" /> Connect {meta.name}
            </button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground block mb-1.5">
                  {meta.apiKeyLabel}
                </label>
                <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                  placeholder="Paste your API key…"
                  className="w-full rounded-xl border border-border text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#0025D8] transition-colors" />
                <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
                  {meta.apiKeyHelp}{" "}
                  <a href={meta.apiKeyLink} target="_blank" rel="noopener noreferrer"
                    className="text-[#0025D8] hover:underline inline-flex items-center gap-0.5">
                    Open settings <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={handleConnect} disabled={connecting || !apiKey.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-mono bg-[#0025D8] text-white hover:bg-[#001DAA] transition-colors disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" />
                  {connecting ? "Connecting…" : "Save & Connect"}
                </button>
                <button onClick={() => { setShowForm(false); setApiKey(""); }}
                  className="px-3 py-2 text-xs font-mono rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── iCIMS card (two-tier: portal URL or full OAuth) ─────────────────────────

const ICIMS_LOGO = (
  <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8">
    <rect width="40" height="40" rx="6" fill="#E8581C" />
    <text x="5" y="27" fontSize="14" fontWeight="900" fill="white" fontFamily="sans-serif">iCIMS</text>
  </svg>
);

function ICIMSCard({ conn, onConnect, onDisconnect, onSync }) {
  const connected = !!conn;
  const hasError = conn?.status === "error";
  const connectedTier = conn?.tier;

  // connection mode selection (before connecting)
  const [selectedTier, setSelectedTier] = useState(1);
  const [showForm, setShowForm] = useState(false);

  // Tier 1 fields
  const [portalUrl, setPortalUrl] = useState("");

  // Tier 2 fields
  const [customerId, setCustomerId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const [connecting, setConnecting] = useState(false);

  const canSubmit = selectedTier === 1
    ? portalUrl.trim().length > 0
    : portalUrl.trim().length > 0 && customerId.trim() && clientId.trim() && clientSecret.trim();

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const payload = { ats_type: "icims", portal_url: portalUrl.trim() };
      if (selectedTier === 2) {
        payload.customer_id = customerId.trim();
        payload.client_id = clientId.trim();
        payload.client_secret = clientSecret.trim();
      }
      await onConnect("icims", payload);
      setShowForm(false);
    } finally { setConnecting(false); }
  };

  const tierBadge = (t) => (
    <span className={`font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] uppercase ${
      connectedTier === t ? "border-[#E8581C]/40 text-[#E8581C] bg-[#E8581C]/5" : "border-border text-muted-foreground"
    }`}>
      Tier {t}
    </span>
  );

  return (
    <div className={`border bg-white col-span-1 md:col-span-2 ${hasError ? "border-[#E11D48]/40" : "border-border"}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-center gap-3">
          {ICIMS_LOGO}
          <div>
            <p className="font-semibold text-sm">iCIMS</p>
            {connected && !hasError && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-[#10B981] tracking-[0.08em]">
                <Check className="w-3 h-3" /> Connected · {connectedTier === 2 ? "REST API (Tier 2)" : "Portal scraping (Tier 1)"}
              </span>
            )}
            {hasError && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#E11D48] tracking-[0.08em]">
                <AlertTriangle className="w-3 h-3" /> Connection error
              </span>
            )}
            {!connected && (
              <span className="font-mono text-[10px] text-muted-foreground tracking-[0.08em]">Not connected</span>
            )}
          </div>
        </div>
        <a href="https://developer.icims.com/" target="_blank" rel="noopener noreferrer"
          className="font-mono text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
          API docs <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {connected ? (
        <ConnectedActions
          name="iCIMS" conn={conn}
          onSync={() => onSync("icims")}
          onDisconnect={() => onDisconnect("icims")}
        />
      ) : (
        <div className="p-5">
          {!showForm ? (
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-foreground hover:border-[#E8581C] hover:text-[#E8581C] transition-all">
              <Plug className="w-3.5 h-3.5" /> Connect iCIMS
            </button>
          ) : (
            <div className="space-y-5">
              {/* Tier selector */}
              <div>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-2">Connection method</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Tier 1 */}
                  <button
                    onClick={() => setSelectedTier(1)}
                    className={`text-left p-3 border transition-all ${selectedTier === 1 ? "border-[#E8581C] bg-[#E8581C]/5" : "border-border hover:border-[#E8581C]/40"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs font-semibold">Tier 1 — Portal URL</span>
                      {selectedTier === 1 && <Check className="w-3.5 h-3.5 text-[#E8581C]" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      No credentials needed. Scrapes your public job board. Works for any iCIMS customer instantly.
                    </p>
                  </button>
                  {/* Tier 2 */}
                  <button
                    onClick={() => setSelectedTier(2)}
                    className={`text-left p-3 border transition-all ${selectedTier === 2 ? "border-[#E8581C] bg-[#E8581C]/5" : "border-border hover:border-[#E8581C]/40"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs font-semibold">Tier 2 — REST API</span>
                      {selectedTier === 2 && <Check className="w-3.5 h-3.5 text-[#E8581C]" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      Full sync via OAuth 2.0. Requires Client ID + Secret from iCIMS admin. More reliable, includes internal roles.
                    </p>
                  </button>
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-3">
                {/* Portal URL — always required */}
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground block mb-1.5">
                    iCIMS Portal URL *
                  </label>
                  <input type="text" value={portalUrl} onChange={(e) => setPortalUrl(e.target.value)}
                    placeholder="e.g. careers.mycompany.icims.com"
                    className="w-full rounded-xl border border-border text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#E8581C] transition-colors" />
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                    Your iCIMS job board URL — found in iCIMS admin → Career Site settings.
                  </p>
                </div>

                {/* Tier 2 extra fields */}
                {selectedTier === 2 && (
                  <>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground block mb-1.5">
                        Customer ID *
                      </label>
                      <input type="text" value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                        placeholder="Numeric tenant ID"
                        className="w-full rounded-xl border border-border text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#E8581C] transition-colors" />
                      <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                        iCIMS admin → System Configuration → Customer ID (numeric).
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground block mb-1.5">
                          Client ID *
                        </label>
                        <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)}
                          placeholder="OAuth Client ID"
                          className="w-full rounded-xl border border-border text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#E8581C] transition-colors" />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground block mb-1.5">
                          Client Secret *
                        </label>
                        <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                          placeholder="OAuth Client Secret"
                          className="w-full rounded-xl border border-border text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#E8581C] transition-colors" />
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      iCIMS admin → Platform → API Access → OAuth Applications → Add Client.{" "}
                      <a href="https://developer.icims.com/iCIMS-Platform-APIs/Authentication" target="_blank" rel="noopener noreferrer"
                        className="text-[#E8581C] hover:underline inline-flex items-center gap-0.5">
                        Setup guide <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </p>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button onClick={handleConnect} disabled={connecting || !canSubmit}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono bg-[#E8581C] text-white hover:bg-[#C44A17] transition-colors disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" />
                  {connecting ? "Connecting…" : "Save & Connect"}
                </button>
                <button onClick={() => setShowForm(false)}
                  className="px-3 py-2 text-xs font-mono rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CompanyIntegrations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role === "company_employee") {
      toast.error("Only company admins can manage integrations");
      navigate("/company/roles", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/ats/connections");
      const map = {};
      for (const conn of data.connections || []) map[conn.ats_type] = conn;
      setConnections(map);
    } catch { toast.error("Could not load integrations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const getAtsName = (atsType) =>
    ATS_META[atsType]?.name || (atsType === "icims" ? "iCIMS" : atsType);

  // payload is { api_key } for GH/Lever, or { portal_url, ... } for iCIMS
  const handleConnect = async (atsType, payload) => {
    try {
      await api.post("/company/ats/connect", { ats_type: atsType, ...payload });
      toast.success(`${getAtsName(atsType)} connected`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect");
      throw err;
    }
  };

  const handleDisconnect = async (atsType) => {
    try {
      await api.delete(`/company/ats/connections/${atsType}`);
      toast.success(`${getAtsName(atsType)} disconnected`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disconnect");
      throw err;
    }
  };

  const handleSync = async (atsType) => {
    try {
      const { data } = await api.post(`/company/ats/sync/${atsType}`);
      const { imported, skipped } = data;
      if (imported === 0) {
        toast.info(`No new roles to import — ${skipped} already exist.`);
      } else {
        toast.success(`Imported ${imported} new role${imported !== 1 ? "s" : ""}${skipped ? ` (${skipped} skipped)` : ""}.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
      throw err;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-white px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
          <Link to="/company/roles" className="hover:text-foreground transition-colors">Roles</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground">Integrations</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-display font-black text-2xl tracking-tight text-[#0F172A] mb-1">ATS Integrations</h1>
          <p className="text-sm text-muted-foreground">
            Connect your Applicant Tracking System to automatically sync open roles into your platform.
            Synced roles are created as drafts — your existing roles are never modified.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-8">
            <div>
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-3">
                Connected ATS Platforms
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Greenhouse + Lever */}
                {Object.keys(ATS_META).map((atsType) => (
                  <ATSCard
                    key={atsType}
                    atsType={atsType}
                    conn={connections[atsType] || null}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onSync={handleSync}
                  />
                ))}
                {/* iCIMS — full-width card (two-tier) */}
                <ICIMSCard
                  conn={connections["icims"] || null}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  onSync={handleSync}
                />
              </div>
            </div>

            {/* Coming soon */}
            {COMING_SOON.length > 0 && (
              <div>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-3">Other Platforms</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {COMING_SOON.map((p) => (
                    <div key={p.name} className="rounded-xl border border-border/50 bg-secondary/20 p-5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-sm text-muted-foreground">{p.name}</p>
                        <span className="font-mono text-[9px] px-2 py-0.5 rounded-xl border border-border text-muted-foreground/60 tracking-[0.12em] uppercase">Coming soon</span>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{p.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How sync works */}
            <div className="rounded-xl border border-border p-5 space-y-3">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">How sync works</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><span className="text-[#0025D8] font-mono text-xs mt-0.5">1.</span> Your credentials are stored securely and never exposed in the UI.</li>
                <li className="flex gap-2"><span className="text-[#0025D8] font-mono text-xs mt-0.5">2.</span> Sync fetches all currently open/published roles from your ATS.</li>
                <li className="flex gap-2"><span className="text-[#0025D8] font-mono text-xs mt-0.5">3.</span> Only roles that don't already exist are created — no duplicates, no overwrites.</li>
                <li className="flex gap-2"><span className="text-[#0025D8] font-mono text-xs mt-0.5">4.</span> Synced roles import title, location, and description. Edit skills, category, and salary after import.</li>
                <li className="flex gap-2"><span className="text-[#E8581C] font-mono text-xs mt-0.5">★</span> <span><strong>iCIMS Tier 1</strong> scrapes your public portal — no credentials needed. <strong>Tier 2</strong> uses the REST API for richer data including internal roles.</span></li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
